/**
 * Book Content Editor
 * 
 * Human-in-the-loop content editing interface for scanned books.
 * This is a wrapper component that handles data loading/saving
 * and delegates to the BlockEditor for the actual editing UI.
 * 
 * Features:
 * - Drag-reorderable digital page blocks
 * - Rich text formatting (indent, bold, italic)
 * - Image region selection for instant re-OCR
 * - Live preview of reading experience
 * - Finish workflow: agent submits for SuperAdmin review (turn_codes_pending)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, AlertCircle, Wand2, Send, X } from 'lucide-react';
import {
  BlockEditor,
  type DigitalPageBlock,
  type ScannedPageData,
  linesToText,
} from '@/components/content-editor';
import { convertToBlocks } from '@/components/content-editor/convertToBlocks';
import {
  loadBookForEditor,
  updatePageContent,
  updateBookBinding,
  publishBook,
  generateReconstructedText,
  shouldPreserveStatus,
  type BookContentForEditor,
  type DigitalPageContent,
} from '@/services/scan/content-editor';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

interface BookContentEditorProps {
  bookId: string;
  onClose?: () => void;
  onPublished?: () => void;
}

// ============================================================================
// Converters
// ============================================================================

// convertToBlocks is now extracted to @/components/content-editor/convertToBlocks.ts
// for testability. It handles 1:1 page-to-block mapping from scanned pages.

/**
 * Convert BlockEditor blocks to storage format.
 */
function convertToDigitalPageContent(blocks: DigitalPageBlock[]): DigitalPageContent[] {
  return blocks.map(block => ({
    sourcePageId: block.sourcePageIds[0] || '',
    text: linesToText(block.lines),
    digitalPageNumber: block.pageNumber,
  }));
}

/**
 * Convert pages to ScannedPageData format for BlockEditor.
 */
function convertToScannedPageData(book: BookContentForEditor): ScannedPageData[] {
  return book.pages.map(page => ({
    id: page.id,
    pageNumber: page.pageNumber,
    imageUrl: page.imageUrl,
    enhancedImageUrl: page.enhancedImageUrl,
    extractedText: page.extractedText,
    pageType: page.pageType,
    isTwoPageSpread: page.isTwoPageSpread,
  }));
}

// ============================================================================
// AI Edit Chat Panel (knowledge articles)
// ============================================================================

interface ChatMsg {
  role: 'system' | 'admin' | 'assistant';
  content: string;
  title?: string;
  star_word?: string;
  takeaway?: string;
}

function AiEditChat({ bookId, onEdited }: { bookId: string; onEdited: () => void }) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('scanned_books')
        .select('title, reconstructed_text, generation_metadata')
        .eq('id', bookId)
        .maybeSingle();

      if (data?.reconstructed_text) {
        const meta = data.generation_metadata as Record<string, unknown> | null;
        setMessages([{
          role: 'system',
          content: data.reconstructed_text,
          title: data.title,
          star_word: meta?.star_word as string | undefined,
          takeaway: meta?.takeaway as string | undefined,
        }]);
      }
      setLoaded(true);
    })();
  }, [bookId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const instruction = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'admin', content: instruction }]);
    setSending(true);

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('generate-knowledge-article', {
        body: { action: 'edit', bookId, instruction },
      });
      if (fnErr) throw fnErr;

      if (data?.status === 'success') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.body,
          title: data.title,
          star_word: data.star_word,
          takeaway: data.takeaway,
        }]);
        onEdited();
        toast.success('Article updated');
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${data?.error || 'Edit failed'}` }]);
      }
    } catch (err) {
      console.error('[AiEditChat] Error:', err);
      Sentry.captureException(err, { tags: { flow: 'knowledge_edit', step: 'send_edit' } });
      setMessages(prev => [...prev, { role: 'assistant', content: 'Failed to process edit. Try again.' }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 text-[#0A84FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-[#D4D4D4] text-sm text-center mt-8">No article content loaded.</p>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`rounded-lg p-3 text-sm ${
            msg.role === 'admin'
              ? 'bg-[#0A84FF]/15 border border-[#0A84FF]/20 ml-6'
              : 'bg-black/[0.03] border border-[#D4D4D4]'
          }`}>
            <p className={`text-xs font-medium mb-1 ${
              msg.role === 'admin' ? 'text-[#0A84FF]' :
              msg.role === 'system' ? 'text-[#888888]' : 'text-[#30D158]'
            }`}>
              {msg.role === 'admin' ? 'You' : msg.role === 'system' ? 'Current article' : 'Claude'}
            </p>
            {msg.title && msg.role !== 'admin' && (
              <p className="font-medium text-[#1A1A1A] mb-1.5">{msg.title}</p>
            )}
            <p className="text-[#1A1A1A] leading-relaxed whitespace-pre-wrap text-[13px]">{msg.content}</p>
            {msg.star_word && (
              <p className="text-xs text-[#0A84FF] mt-2">Star word: {msg.star_word}</p>
            )}
            {msg.takeaway && (
              <p className="text-xs text-[#888888] mt-1">Takeaway: {msg.takeaway}</p>
            )}
          </div>
        ))}
        {sending && (
          <div className="rounded-lg p-3 bg-black/[0.03] border border-[#D4D4D4]">
            <p className="text-[#30D158] text-xs font-medium mb-1">Claude</p>
            <div className="flex items-center gap-2 text-[#888888] text-sm">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Editing...
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input bar */}
      <div className="p-3 border-t border-[#D4D4D4] flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
          placeholder="Tell Claude what to change..."
          className="flex-1 px-3 py-2.5 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm placeholder-[#D4D4D4] focus:outline-none focus:border-[#0A84FF]"
          disabled={sending}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="px-3 py-2.5 rounded-lg bg-[#0A84FF] text-[#1A1A1A] transition-colors disabled:opacity-40 hover:brightness-110"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function BookContentEditor({ bookId, onClose, onPublished }: BookContentEditorProps) {
  const [book, setBook] = useState<BookContentForEditor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAiChat, setShowAiChat] = useState(false);
  const [isKnowledge, setIsKnowledge] = useState(false);

  // Load book data
  useEffect(() => {
    async function load() {
      try {
        const data = await loadBookForEditor(bookId);
        if (!data) {
          setError('Book not found');
          return;
        }
        setBook(data);

        // Separate query to reliably detect knowledge articles
        const { data: meta } = await supabase
          .from('scanned_books')
          .select('content_source')
          .eq('id', bookId)
          .maybeSingle();
        setIsKnowledge(meta?.content_source === 'generated');
      } catch (err) {
        console.error('[BookContentEditor] Error loading:', err);
        Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'load_book' } });
        setError('Failed to load book');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [bookId]);

  // Save blocks to database
  const handleSave = useCallback(async (blocks: DigitalPageBlock[]) => {
    if (!book) return;
    
    const digitalPageContent = convertToDigitalPageContent(blocks);
    
    const updatePayload: Record<string, unknown> = {
      digital_page_content: digitalPageContent as any,
    };
    if (!shouldPreserveStatus(book.qualityStatus)) {
      updatePayload.quality_status = 'needs_review';
    }

    const { error: saveError } = await supabase
      .from('scanned_books')
      .update(updatePayload)
      .eq('id', bookId);
    
    if (saveError) {
      throw new Error(`Failed to save: ${saveError.message}`);
    }
    
    console.log(`[BookContentEditor] Saved ${blocks.length} blocks`);
  }, [book, bookId]);

  // Publish book
  const handlePublish = useCallback(async (blocks: DigitalPageBlock[]) => {
    if (!book) return;
    
    const digitalPageContent = convertToDigitalPageContent(blocks);
    
    const fullText = blocks
      .map(block => linesToText(block.lines))
      .join('\n\n');
    
    const { error: textError } = await supabase
      .from('scanned_books')
      .update({ reconstructed_text: fullText })
      .eq('id', bookId);
    if (textError) {
      Sentry.captureException(new Error(textError.message), { tags: { flow: 'scan_agent', step: 'save_reconstructed_text' }, extra: { bookId } });
      throw new Error(`Failed to save text: ${textError.message}`);
    }
    
    const result = await publishBook(bookId, digitalPageContent);

    if (result.success) {
      console.log(`[BookContentEditor] Published → turn_codes_pending`);
      onPublished?.();
    }
  }, [book, bookId, onPublished]);

  // Reload blocks after AI edit so the text panel updates
  const handleAiEdited = useCallback(async () => {
    try {
      const data = await loadBookForEditor(bookId);
      if (data) setBook(data);
    } catch (err) {
      console.error('[BookContentEditor] Reload after AI edit failed:', err);
    }
  }, [bookId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#0A84FF] animate-spin" />
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <div className="p-6 rounded-2xl bg-[#FF453A]/10 border border-[#FF453A]/20 text-center">
          <AlertCircle className="w-12 h-12 text-[#FF453A] mx-auto mb-4" />
          <div className="text-[#1A1A1A] font-medium mb-2">{error || 'Book not found'}</div>
          {onClose && (
            <button onClick={onClose} className="text-[#0A84FF] text-sm">
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  const BINDING_OPTIONS = ['Hardcover', 'Paperback', 'Board Book', 'Library Binding', 'Spiral-bound'];

  const handleBindingChange = async (newBinding: string) => {
    const value = newBinding || null;
    try {
      await updateBookBinding(bookId, value);
      setBook(prev => prev ? { ...prev, binding: value } : prev);
    } catch (err) {
      console.error('[BookContentEditor] Failed to update binding:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'update_binding' } });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[#D4D4D4] flex-shrink-0">
        <label className="text-xs text-[#888888]">Edition:</label>
        <select
          value={book.binding || ''}
          onChange={e => handleBindingChange(e.target.value)}
          className="px-2 py-1 rounded-lg bg-black/[0.03] border border-[#D4D4D4] text-[#1A1A1A] text-xs focus:outline-none focus:ring-1 focus:ring-[#0A84FF]/50"
        >
          <option value="">Not set</option>
          {BINDING_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>

        {isKnowledge && (
          <button
            onClick={() => setShowAiChat(c => !c)}
            className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              showAiChat
                ? 'bg-[#0A84FF] text-white'
                : 'bg-[#0A84FF]/15 text-[#0A84FF] hover:bg-[#0A84FF]/25'
            }`}
          >
            <Wand2 className="w-3.5 h-3.5" />
            AI Editor
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* Main editor */}
        <div className={`flex-1 min-w-0 ${showAiChat ? 'border-r border-[#D4D4D4]' : ''}`}>
          <BlockEditor
            key={book.reconstructedText?.length}
            bookId={bookId}
            bookTitle={book.title}
            bookAuthor={book.author}
            scannedPages={convertToScannedPageData(book)}
            initialBlocks={convertToBlocks(book)}
            onSave={handleSave}
            onPublish={handlePublish}
            onClose={onClose}
          />
        </div>

        {/* AI chat side panel */}
        {showAiChat && (
          <div className="w-[380px] flex-shrink-0 flex flex-col bg-black/30">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#D4D4D4]">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-[#0A84FF]" />
                <span className="text-sm font-semibold text-[#1A1A1A]">AI Editor</span>
              </div>
              <button onClick={() => setShowAiChat(false)} className="text-[#D4D4D4] hover:text-[#3D3D3D]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <AiEditChat bookId={bookId} onEdited={handleAiEdited} />
          </div>
        )}
      </div>
    </div>
  );
}

export default BookContentEditor;
